// Re-run a screen's catch-up read when the device comes back.
//
// The gap this closes: broadcast and postgres_changes are both LIVE-only. Neither
// replays. A phone that was asleep, backgrounded, or off the venue wifi while the
// GM pressed hears nothing — and if the component never remounts (which it
// doesn't, because the room is still on the same step), the on-mount catch-up
// never runs again either. That player sits on a stale screen until the GM
// advances past it. Every resilience layer in this app protects the DELIVERY of
// an event; none of them noticed that a client which missed one had no way to
// ask.
//
// So: on focus, on return to visibility, on `online`, and on a channel
// resubscribe, ask the database again.
//
// Since the reveal and the podium started confirming broadcasts against the
// persisted row, callers can also ask for a slow HEARTBEAT while they wait.
// Every delivery path can fail at once — the socket is down and the host's
// write was replayed after a reload, so no broadcast was ever sent for it
// (Codex) — and an event-driven net cannot fire when nothing happens on the
// device. A phone sitting still on a waiting screen is exactly the case nothing
// else covers.
//
// Throttled and jittered for the same reason GameFlowProvider's refocus resync
// is (TAS-65 / TAS-271 PR-5): one tab refocus fires BOTH `focus` and
// `visibilitychange`, and a co-located party all picking their phones up on the
// same beat would otherwise burst N identical queries at the backend in the same
// instant. The leading edge is jittered rather than immediate because nothing
// here is latency-critical — the fast paths already ran; this is the net below
// them.

import { useEffect, useRef } from 'react';
import { jitterDelay } from '../utils/realtime';

const THROTTLE_MS = 500;
const JITTER_MS = 1500;

/**
 * @param {boolean}  enabled   false while the screen has nothing to catch up on
 * @param {Function} onRecover (trigger: string) => void — safe to call spuriously
 * @param {object}  [options]
 * @param {number}  [options.pollMs] re-ask this often while the screen waits and
 *                                   the tab is visible. 0 (default) = no polling.
 */
export function useCatchUpOnRecovery(enabled, onRecover, { pollMs = 0 } = {}) {
  // Held in a ref so a caller doesn't have to memoise its handler to avoid
  // tearing down and rebinding four listeners on every render.
  const handlerRef = useRef(onRecover);
  useEffect(() => { handlerRef.current = onRecover; }, [onRecover]);

  useEffect(() => {
    if (!enabled) return undefined;
    if (typeof window === 'undefined') return undefined;

    let disposed = false;
    let lastAt = 0;
    let pending = null;

    const trigger = (reason) => {
      const now = Date.now();
      if (now - lastAt < THROTTLE_MS) return;
      lastAt = now;
      if (pending) return; // one already queued — it will read the latest state
      pending = setTimeout(() => {
        pending = null;
        if (disposed) return;
        handlerRef.current?.(reason);
      }, jitterDelay(JITTER_MS));
    };

    const onFocus = () => trigger('focus');
    const onOnline = () => trigger('online');
    const onVisibility = () => {
      if (document.visibilityState === 'visible') trigger('visibility');
    };

    window.addEventListener('focus', onFocus);
    window.addEventListener('online', onOnline);
    document.addEventListener('visibilitychange', onVisibility);

    // Visible only: a backgrounded phone is throttled anyway, and it gets a
    // visibility trigger the moment it comes back.
    const poll = pollMs > 0
      ? setInterval(() => {
          if (document.visibilityState !== 'visible') return;
          trigger('poll');
        }, pollMs)
      : null;

    return () => {
      disposed = true;
      if (pending) clearTimeout(pending);
      if (poll) clearInterval(poll);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('online', onOnline);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [enabled, pollMs]);
}
